import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Gruppe, Session, Runde, Punkt } from '../types';
import { ApiService } from '../api';
import Icon from './Icon';
import SegmentedControl from './SegmentedControl';
import ActionSheet from './ActionSheet';
import { useToast } from './Toast';

interface SessionSpielProps {
  session: Session;
  gruppe: Gruppe;
  onBack: () => void;
  onRefresh: () => void;
}

type Tab = 'spielen' | 'runden';

const avatarColor = (id: number) => {
  const colors = ['#0a84ff', '#bf5af2', '#ff2d55', '#30d158', '#ff9f0a', '#63e6e2', '#5e5ce6', '#ffd60a'];
  return colors[id % colors.length];
};

const initials = (name: string) =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((s) => s.charAt(0).toUpperCase())
    .join('') || '?';

const SessionSpiel: React.FC<SessionSpielProps> = ({ session, gruppe, onBack, onRefresh }) => {
  const [tab, setTab] = useState<Tab>('spielen');
  const [runden, setRunden] = useState<Runde[]>([]);
  const [currentRunde, setCurrentRunde] = useState<Runde | null>(null);
  const [punkte, setPunkte] = useState<{ [spielerId: number]: number }>({});
  const [caboSpielerId, setCaboSpielerId] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [gameOverInfo, setGameOverInfo] = useState<{ winner?: string; points?: number } | null>(null);
  const toast = useToast();

  const resetInputs = useCallback(() => {
    const init: { [spielerId: number]: number } = {};
    gruppe.spieler.forEach((s) => (init[s.id] = 0));
    setPunkte(init);
    setCaboSpielerId(null);
  }, [gruppe.spieler]);

  const loadRunden = useCallback(async () => {
    try {
      const data = await ApiService.getRunden(session.id);
      setRunden(data);
      resetInputs();
    } catch (e) {
      console.error(e);
    }
  }, [session.id, resetInputs]);

  useEffect(() => {
    loadRunden();
  }, [loadRunden]);

  const getTotal = useCallback(
    (spielerId: number, withDraft = false): number => {
      let sum = runden.reduce((acc, r) => {
        if (currentRunde && r.id === currentRunde.id) return acc;
        const ps = (r.punkte || []).filter((p) => p.spieler_id === spielerId);
        return acc + ps.reduce((a, p) => a + p.wert, 0);
      }, 0);
      if (withDraft) sum += punkte[spielerId] || 0;
      return sum;
    },
    [runden, currentRunde, punkte]
  );

  const isGameOver = useMemo(() => {
    if (session.ended_at) return true;
    return gruppe.spieler.some((s) => getTotal(s.id) > 100);
  }, [session.ended_at, gruppe.spieler, getTotal]);

  const leader = useMemo(() => {
    if (gruppe.spieler.length === 0) return null;
    const arr = gruppe.spieler.map((s) => ({ name: s.name, total: getTotal(s.id) }));
    const min = Math.min(...arr.map((x) => x.total));
    const names = arr.filter((x) => x.total === min).map((x) => x.name);
    return { names, total: min };
  }, [gruppe.spieler, getTotal]);

  const handleStartRunde = async () => {
    if (gruppe.spieler.length === 0) {
      toast.error('Keine Spieler vorhanden');
      return;
    }
    setIsSaving(true);
    try {
      const neu = await ApiService.createRunde(session.id, runden.length + 1, undefined);
      setCurrentRunde(neu);
      setRunden((prev) => [...prev, neu]);
      resetInputs();
    } catch (e) {
      console.error(e);
      toast.error('Runde konnte nicht gestartet werden');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = async () => {
    if (!currentRunde) return;
    setIsSaving(true);
    try {
      if (caboSpielerId) {
        await ApiService.updateRunde(currentRunde.id, currentRunde.nummer, caboSpielerId);
      }
      const arr: Punkt[] = gruppe.spieler.map((s) => ({ spieler_id: s.id, wert: punkte[s.id] || 0 }));
      await ApiService.setPunkte(currentRunde.id, arr);

      const freshRunden = await ApiService.getRunden(session.id);
      setRunden(freshRunden);

      const totals = gruppe.spieler.map((s) => {
        const sum = freshRunden.reduce((acc, r) => {
          const ps = (r.punkte || []).filter((p) => p.spieler_id === s.id);
          return acc + ps.reduce((a, p) => a + p.wert, 0);
        }, 0);
        return { name: s.name, total: sum };
      });

      const highest = totals.reduce((a, b) => (a.total > b.total ? a : b));

      if (highest.total > 100) {
        await ApiService.updateSession(session.id, new Date().toISOString());
        setGameOverInfo({ winner: highest.name, points: highest.total });
        setCurrentRunde(null);
        resetInputs();
        onRefresh();
        return;
      }

      const nextRunde = await ApiService.createRunde(session.id, freshRunden.length + 1, undefined);
      setCurrentRunde(nextRunde);
      setRunden((prev) => [...prev, nextRunde]);
      resetInputs();
      toast.success(`Runde ${currentRunde.nummer} gespeichert`);
    } catch (e) {
      console.error(e);
      toast.error('Speichern fehlgeschlagen');
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditRunde = async (r: Runde) => {
    try {
      const punkteData = await ApiService.getPunkte(r.id);
      const map: { [spielerId: number]: number } = {};
      punkteData.forEach((p) => (map[p.spieler_id] = p.wert));
      setPunkte(map);
      setCaboSpielerId(r.cabo_spieler_id ?? null);
      setCurrentRunde({ ...r, punkte: punkteData });
      setTab('spielen');
    } catch (e) {
      console.error(e);
    }
  };

  const handleCancelRound = () => {
    setCurrentRunde(null);
    resetInputs();
  };

  const updatePunkte = (spielerId: number, value: number) => {
    setPunkte((prev) => ({ ...prev, [spielerId]: value }));
  };

  const getCellDisplay = (r: Runde, spielerId: number): { text: string; className: string } => {
    if (currentRunde?.id === r.id) {
      const v = punkte[spielerId] ?? 0;
      return { text: String(v), className: 'cell-draft' };
    }
    const ps = (r.punkte || []).filter((p) => p.spieler_id === spielerId);
    const wert = ps.reduce((a, p) => a + p.wert, 0);
    const hatKorrektur = ps.some((p) => p.is_korrektur);
    if (ps.length === 0) {
      return { text: '—', className: '' };
    }
    let cls = '';
    if (wert > 100) cls = 'cell-danger';
    else if (hatKorrektur) cls = 'cell-korrektur';
    return { text: `${wert}${hatKorrektur ? ' *' : ''}`, className: cls };
  };

  const totalWithLiveDraft = (spielerId: number) => getTotal(spielerId, currentRunde != null);

  const roundsTableEl = (compact: boolean) => {
    const cls = compact ? 'rounds-table rounds-table--compact' : 'rounds-table';
    if (runden.length === 0 && !currentRunde) {
      return (
        <div className={cls}>
          <div className="session-play-sidebar-empty" style={{ margin: 8 }}>
            Noch keine abgeschlossenen Runden.
          </div>
        </div>
      );
    }
    return (
      <div className={cls}>
        <div className="rounds-scroll">
          <table>
            <thead>
              <tr>
                <th>Runde</th>
                {gruppe.spieler.map((s) => (
                  <th key={s.id}>{s.name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {runden.map((r) => (
                <tr key={r.id} className={currentRunde?.id === r.id ? 'rounds-row-live' : undefined}>
                  <td>
                    {currentRunde?.id === r.id ? (
                      <span className="round-live-badge">
                        <span className="round-live-dot" aria-hidden />
                        #{r.nummer}
                      </span>
                    ) : (
                      `#${r.nummer}`
                    )}
                  </td>
                  {gruppe.spieler.map((s) => {
                    const disp = getCellDisplay(r, s.id);
                    return (
                      <td key={s.id} className={disp.className}>
                        {disp.text}
                      </td>
                    );
                  })}
                </tr>
              ))}
              <tr className="total-row">
                <td>Gesamt</td>
                {gruppe.spieler.map((s) => {
                  const total = totalWithLiveDraft(s.id);
                  return (
                    <td key={s.id} className={total > 100 ? 'cell-danger' : ''}>
                      {total}
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const roundsTableFooterEl = (
    <div className="section-footer" style={{ padding: '6px 0 0' }}>
      <span style={{ color: 'var(--ios-orange)' }}>*</span> = 100-P.-Regel (Reset auf 50).
      {currentRunde && (
        <span className="muted" style={{ marginLeft: 8 }}>
          Grüner Punkt = aktuelle Eingabe (noch nicht gespeichert).
        </span>
      )}
    </div>
  );

  return (
    <>
      <div className="bento-grid">
        <div className="bento glassy">
          <div className="bento-label">Runde</div>
          <div className="bento-value">{currentRunde ? currentRunde.nummer : runden.length}</div>
          <div className="bento-sub">{currentRunde ? 'aktiv' : 'gespielt'}</div>
        </div>
        <div className="bento accent">
          <div className="bento-label">Führend</div>
          <div className="bento-value" style={{ fontSize: 18, lineHeight: 1.2 }}>
            {leader?.names.join(', ') || '—'}
          </div>
          <div className="bento-sub">{leader ? `${leader.total} Punkte` : ''}</div>
        </div>
      </div>

      <SegmentedControl<Tab>
        value={tab}
        onChange={setTab}
        options={[
          { id: 'spielen', label: currentRunde ? `Runde ${currentRunde.nummer}` : 'Spielen' },
          { id: 'runden', label: `Runden · ${runden.length}` },
        ]}
      />

      {tab === 'spielen' && (
        <div className="session-play-split">
          <div className="session-play-main">
            {!currentRunde ? (
              <div className="lg" style={{ padding: 20, textAlign: 'center' }}>
                <div style={{ fontSize: 42, marginBottom: 10 }}>🎴</div>
                <div className="title-2" style={{ marginBottom: 4 }}>
                  {isGameOver ? 'Spiel beendet' : 'Bereit für die nächste Runde?'}
                </div>
                <div className="footnote" style={{ marginBottom: 16 }}>
                  {isGameOver
                    ? 'Der Spielstand ist final. Keine weiteren Runden möglich.'
                    : `${runden.length} Runden gespielt · ${gruppe.spieler.length} Spieler`}
                </div>
                {!isGameOver && (
                  <button
                    className="btn btn-primary btn-block"
                    onClick={handleStartRunde}
                    disabled={isSaving}
                  >
                    <Icon name="play" size={18} />
                    {isSaving ? 'Starte…' : 'Runde starten'}
                  </button>
                )}
                {isGameOver && (
                  <button className="btn btn-tinted btn-block" onClick={onBack}>
                    Zurück zur Gruppe
                  </button>
                )}
              </div>
            ) : (
              <>
                <div className="section-header">
                  Cabo gerufen von
                  <span className="muted" style={{ marginLeft: 6, textTransform: 'none', fontWeight: 400 }}>
                    · Bonus/Strafe wird automatisch berechnet
                  </span>
                </div>

                <div className="players-grid">
                  {gruppe.spieler.map((spieler) => {
                    const isCabo = caboSpielerId === spieler.id;
                    return (
                      <div key={spieler.id} className={`player-card ${isCabo ? 'cabo-rufer' : ''}`}>
                        {isCabo && <div className="cabo-badge">CABO</div>}
                        <div
                          className="player-avatar"
                          style={{ background: `linear-gradient(135deg, ${avatarColor(spieler.id)}, ${avatarColor(spieler.id + 2)})` }}
                        >
                          {initials(spieler.name)}
                        </div>
                        <div className="player-name">{spieler.name}</div>
                        <div className="player-total">
                          Gesamt: {getTotal(spieler.id, true)}
                        </div>
                        <input
                          className="score-input"
                          type="number"
                          inputMode="numeric"
                          value={punkte[spieler.id] ?? 0}
                          onFocus={(e) => e.target.select()}
                          onChange={(e) => updatePunkte(spieler.id, parseInt(e.target.value) || 0)}
                          min={0}
                          max={200}
                        />
                        <button
                          type="button"
                          className={`btn btn-sm ${isCabo ? 'btn-primary' : 'btn-gray'}`}
                          onClick={() => setCaboSpielerId(isCabo ? null : spieler.id)}
                          style={{ justifyContent: 'center' }}
                        >
                          <Icon name="target" size={14} />
                          {isCabo ? 'Cabo gerufen' : 'Cabo rufen'}
                        </button>
                      </div>
                    );
                  })}
                </div>

                <div style={{ display: 'flex', gap: 8, flexDirection: 'column' }}>
                  <button
                    className="btn btn-primary btn-block"
                    onClick={handleSave}
                    disabled={isSaving}
                  >
                    <Icon name="check" size={18} />
                    {isSaving ? 'Speichere…' : `Runde ${currentRunde.nummer} speichern`}
                  </button>
                  <button className="btn btn-gray btn-block" onClick={handleCancelRound} disabled={isSaving}>
                    Abbrechen
                  </button>
                </div>
              </>
            )}
          </div>

          <aside className="session-play-sidebar" aria-label="Rundentabelle und Gesamtstand">
            <div className="session-play-sidebar-title">Spielstand</div>
            {roundsTableEl(true)}
            {roundsTableFooterEl}
          </aside>
        </div>
      )}

      {tab === 'runden' && (
        <>
          <div className="section-header">Spielstand</div>
          {roundsTableEl(false)}
          {roundsTableFooterEl}

          {runden.length > 0 && (
            <>
              <div className="section-header">Runde bearbeiten</div>
              <div className="ios-list">
                {runden.map((r) => (
                  <div key={r.id} className="ios-row interactive" onClick={() => handleEditRunde(r)}>
                    <div className="row-icon"><Icon name="pencil" size={14} /></div>
                    <div className="row-main">
                      <div className="row-title">Runde {r.nummer}</div>
                      <div className="row-subtitle">
                        Summe: {gruppe.spieler.reduce((acc, sp) => {
                          const ps = (r.punkte || []).filter((p) => p.spieler_id === sp.id);
                          return acc + ps.reduce((a, p) => a + p.wert, 0);
                        }, 0)}
                      </div>
                    </div>
                    <Icon name="chevron-right" size={16} className="chevron" />
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}

      <ActionSheet
        open={!!gameOverInfo}
        onClose={() => setGameOverInfo(null)}
        title="🏁 Spiel beendet"
        message={gameOverInfo ? `${gameOverInfo.winner} hat ${gameOverInfo.points} Punkte erreicht.` : undefined}
        actions={[{ label: 'Okay', style: 'cancel' }]}
      />
    </>
  );
};

export default SessionSpiel;
